import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Section: Site header
 * ------------------------------------------------------------------------------
 * Contains highly coupled scripts to control site header components.
 *
 * @namespace siteHeader
 */

import { register } from '@shopify/theme-sections';
import Cookies from 'js-cookie';
import Notification from '~/components/notification';
import Toggle from '~/components/toggle';
import cssClasses from '~/helpers/cssClasses';
import { on } from '~/helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  siteHeaderWrapper: '[js-site-header="wrapper"]',
  container: '[js-site-header="container"]',
  dropdownParent: '[js-site-dropdown="dropdownParent"]',
  megaNavToggle: '[js-site-header="megaNavToggle"]',
  menuDrawer: '[js-site-header="menuDrawer"]',
  navLink: '[js-site-header="navLink"]',
  searchBarHeader: '[js-site-header="searchBar"]',
  searchBarClear: '[js-predictive-search="reset"]',
  siteNavItem: '[js-site-header="siteNavItem"]',
  siteNavLink: '[js-site-header="siteNavLink"]',
  storeIcon: '[js-site-header="storeIcon"]',
  hamburger: '[js-site-header="hamburger"]'
};
/**
 * Instance cookies.
 */

const cookieSelector = {
  geolocation: 'redirect_geolocation',
  settings: 'redirect_store_settings'
};
/**
 * Site header custom toggle classes.
 */

const customClasses = {
  meganavOpen: 'meganav-open',
  isVisible: 'is-visible'
};
const hideMegaNavTimer = 500;
let blurTimer;
let lastScrollTop = 0;
/**
 * Register the `site-header` section type.
 */

register('site-header', {
  /**
   * Callback function when the section is loaded in via the  `sections.load()`
   * function or by the Theme Editor `shopify:section:unload` event.
   */
  onLoad() {
    this.constructComponents();
    this.init();
  },

  /**
   * Component selectors.
   */
  setNodeSelectors() {
    this.siteHeaderWrapper = document.querySelector(selectors.siteHeaderWrapper);
    this.container = document.querySelector(selectors.container);
    this.dropdownParent = [...document.querySelectorAll(selectors.dropdownParent)];
    this.megaNavToggle = [...document.querySelectorAll(selectors.megaNavToggle)];
    this.navLink = [...document.querySelectorAll(selectors.navLink)];
    this.searchBarHeader = document.querySelector(selectors.searchBarHeader);
    this.searchBarClear = document.querySelector(selectors.searchBarClear);
    this.storeIcon = document.querySelector(selectors.storeIcon);
    this.menuDrawer = document.querySelector(selectors.menuDrawer);
    this.hamburger = document.querySelector(selectors.hamburger);
  },

  /**
   * Component variables.
   */
  setVariables() {
    this.ariaExpandedString = 'aria-expanded';
    this.blurTimer = blurTimer;
    this.hideMegaNavTimer = hideMegaNavTimer;
  },

  /**
   * Instantiate search bar toggle.
   */
  constructSearchToggle() {
    this.SearchBar = Toggle({
      exclude: ['[data-target="searchBarHeader"]'],
      toggleSelector: 'searchBarHeader'
    });
  },

  /**
   * Instantiate announcement component.
   */
  constructAnnouncement() {
    this.Announcement = Notification({
      container: '.site-header__announcement',
      enableCookies: true,
      cookie: {
        namespace: 'frame_announcement',
        value: 'dismissed'
      }
    });
  },

  /**
   * Construct section components.
   */
  constructComponents() {
    this.constructSearchToggle();
    this.constructAnnouncement();
  },

  /**
   * Initialise component.
   */
  init() {
    this.setNodeSelectors();
    this.setEventListeners();
    this.setEventBusListeners();
    this.setSearchToggle();
    this.setFixedHeader();

    if (window.location.pathname === '/') {
      this.setHeaderTransparency();
    } else {
      this.updateBodyOffset();
    }
  },

  /**
   * Set search toggle events.
   */
  setSearchToggle() {
    this.SearchBar.init();
  },

  /**
   * Set fixed header on scroll.
   */
  setFixedHeader() {
    on('scroll', () => {
      const scrollTop = window.pageYOffset || 0;

      if (scrollTop > lastScrollTop && scrollTop > 110) {
        this.siteHeaderWrapper.classList.add(cssClasses.sticky, cssClasses.collapsed);
      } else {
        this.siteHeaderWrapper.classList.remove(cssClasses.collapsed);
      }

      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    });
  },

  /**
   * Set transparency on homepage header.
   */
  setHeaderTransparency() {
    const className = 'site-header--transparent';
    this.container.classList.add(className);
    on('scroll', () => {
      const scrollTop = window.pageYOffset || 0;

      if (scrollTop > lastScrollTop && scrollTop > 110) {
        setTimeout(() => {
          this.container.classList.remove(className);
        }, 500);
      } else {
        this.container.classList.remove(className);
      }

      if (scrollTop <= 110) {
        this.container.classList.add(className);
      }

      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    });
  },

  /**
   * Re-paint body padding-top using current header height.
   */
  updateBodyOffset() {
    if (window.location.pathname !== '/') {
      document.body.style.paddingTop = "".concat(this.container.offsetHeight, "px");
    }
  },

  /**
   * Get store icon using cookies and other stored information.
   * @returns {String}
   */
  getStoreIcon() {
    const storeSettings = Cookies.getJSON(cookieSelector.settings);
    const storeCode = typeof storeSettings === 'undefined' ? theme.store.storeCode : storeSettings.loc;
    return storeCode.toLowerCase();
  },

  /**
   * Update the icon based on the user's store settings.
   * @param {String} countryCode - The country code.
   */
  setStoreIcon() {
    let countryCode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.getStoreIcon();
    this.storeIcon.className = "flag-icon flag-icon-".concat(countryCode);
  },

  /**
   * Cancels the blur timer if the focus is still on a Nav Link item.
   * @param {HTMLElement} element - The Mega Nav link element
   */
  setKeyFocus(element) {
    on('focus', element, () => window.clearTimeout(this.blurTimer));
  },

  /**
   * Sets a timer that hides the Mega Nav if no Site Nav element is focused.
   * @param {HTMLElement} element - The Mega Nav link element.
   */
  setKeyBlur(element) {
    const siteNavParent = this.getSiteNavParent(element);
    on('blur', element, () => {
      this.blurTimer = window.setTimeout(() => this.hideMegaNav(siteNavParent), hideMegaNavTimer);
    });
  },

  /**
   * Hides active sibling Mega Nav items and sets aria-expanded to false using hideMegaNav function.
   * @param {HTMLElement} element - The Mega Nav toggle button.
   */
  hideSiblingMegaNavs(element) {
    const siteHeader = element.parentNode;
    siteHeader.childNodes.forEach(child => {
      if (child !== element && child.nodeType === 1 && child.classList.contains(cssClasses.active)) {
        this.hideMegaNav(child);
      }

      return element;
    });
  },

  /**
   * Displays a Mega Nav item and sets aria-expanded to true.
   * @param {HTMLElement} siteNavParent - The Mega Nav.
   */
  showMegaNav(siteNavParent) {
    siteNavParent.classList.add(cssClasses.active);
    siteNavParent.querySelector(selectors.siteNavLink).setAttribute(this.ariaExpandedString, true);
    siteNavParent.querySelector(selectors.megaNavToggle).setAttribute(this.ariaExpandedString, true);
  },

  /**
   * Hides a Mega Nav item and sets aria-expanded to false.
   * @param {HTMLElement} siteNavParent - The Mega Nav.
   */
  hideMegaNav(siteNavParent) {
    siteNavParent.classList.remove(cssClasses.active);
    siteNavParent.querySelector(selectors.siteNavLink).setAttribute(this.ariaExpandedString, false);
    siteNavParent.querySelector(selectors.megaNavToggle).setAttribute(this.ariaExpandedString, false);
  },

  /**
   * Selects the siteNav parent.
   * @param {HTMLElement} element - The Nav List item or the megaNavToggle button.
   * @returns {HTMLElement}
   */
  getSiteNavParent(element) {
    return element.closest(selectors.siteNavItem);
  },

  /**
   * Add transparent class on header masthead
   */
  handleMenuDrawerClose() {
    const scrollTop = window.pageYOffset || 0;

    if (scrollTop <= 110) {
      this.container.classList.add('site-header--transparent');
    }

    this.container.classList.add('is-drawer-closed');
    this.container.classList.remove('is-drawer-open');
  },

  /**
   * Remove transparent class on header masthead
   */
  handleMenuDrawerOpen() {
    const scrollTop = window.pageYOffset || 0;

    if (scrollTop >= 110) {
      this.container.classList.remove('site-header--transparent');
    }

    this.container.classList.remove('site-header--transparent');
    this.container.classList.remove('is-drawer-closed');
    this.container.classList.add('is-drawer-open');
    this.siteHeaderWrapper.classList.remove('is-collapsed');
    this.SearchBar.close(this.searchBarHeader);
  },

  /**
   * Set event bus listeners based on scroll direction.
   */
  setEventListeners() {
    this.dropdownParent.forEach(element => {
      on('mouseover', element, () => {
        if (!this.searchBarHeader.getAttribute('js-search-bar-active')) {
          return;
        }

        this.SearchBar.close(this.searchBarHeader);
      });
      on('mouseenter', element, () => {
        this.container.classList.add(customClasses.meganavOpen);
        element.classList.add(cssClasses.active);
      });
      on('mouseleave', element, () => {
        this.navHoverTimer = setTimeout(() => {
          this.container.classList.remove(customClasses.meganavOpen);
          element.classList.remove(cssClasses.active);
        }, 250);
      });
    });
    on('click', this.searchBarClear, event => {
      event.preventDefault();
    });
    on('click', this.hamburger, element => {
      element.target.classList.toggle('open');
    });
  },

  /**
   * Set event bus listeners based on scroll direction.
   */
  setEventBusListeners() {
    Frame.EventBus.listen('Notification:dismissed', () => {
      this.updateBodyOffset();
    });
    Frame.EventBus.listen('ScrollDirection:down', () => {
      this.siteHeaderWrapper.classList.add(cssClasses.collapsed);
    });
    Frame.EventBus.listen('ScrollDirection:top', () => {
      this.siteHeaderWrapper.classList.remove(cssClasses.collapsed);
    });
    Frame.EventBus.listen('Toggle:searchBarHeader:open', () => {
      document.documentElement.classList.add('js-search-active');
      this.searchBarHeader.setAttribute('js-search-bar-active', true);
    });
    Frame.EventBus.listen('Toggle:searchBarHeader:close', closeTarget => {
      /**
       * Delay state reset if target is not an overlay.
       */
      if (typeof closeTarget === 'undefined') {
        document.documentElement.classList.remove('js-search-active');
        this.searchBarHeader.setAttribute('js-search-bar-active', false);
        return;
      }

      setTimeout(() => {
        document.documentElement.classList.remove('js-search-active');
        this.searchBarHeader.setAttribute('js-search-bar-active', false);
      }, 500);
    });
    Frame.EventBus.listen('Redirect:updateIcon', countryCode => {
      this.setStoreIcon(countryCode);
    });
    Frame.EventBus.listen(['Toggle:menuDrawer:open'], () => this.handleMenuDrawerOpen());
    Frame.EventBus.listen(['Toggle:menuDrawer:close'], () => this.handleMenuDrawerClose());
    this.navLink.forEach(element => {
      this.setKeyBlur(element);
      this.setKeyFocus(element);
    });
  }

});